/**
 * BundleProductModal Class Functions:
 *
 * constructor() - Initializes the modal, sets up event listeners and initial state
 *
 * disconnectedCallback() - Cleans up event listeners when element is removed from DOM
 *
 * setButtonAccessibility() - Sets up click handlers for bundle variant selector buttons
 *
 * syncBundleInputs() - Keeps the hidden line item property inputs in sync with the selected bundle items
 *
 * handleBundleFormSubmit() - Writes the __privateBundle* cart attributes at add-to-cart time
 *
 * updateModalContent(product) - Updates modal content with product details
 *
 * createVariantSelector() - Creates and populates the variant selector dropdown
 *
 * confirmVariant() - Handles variant selection confirmation
 *
 * open(button) - Opens the modal and sets focus on trigger button
 *
 * close() - Closes the modal and returns focus
 *
 * handleOutsideClick() - Closes variant selector when clicking outside
 *
 * updateSubmitButtonStatus() - Updates disabled state of the main add-to-cart button
 *
 * updateConfirmButtonStatus() - Updates disabled state of the confirm button based on selected variant availability
 *
 * updateRatingSnippet() - Updates Reviews.io rating snippet
 */

class BundleProductModal extends HTMLElement {
  constructor() {
    super();
    this.previouslySelectedVariant = [];
    this.selectedVariant = null;
    this.currencyFormat = document.querySelector('.price-item').textContent.replace(/[\d.,]/g, '').trim();  // Remove numbers, commas, and dots

    this.updateBundlePrice();
    this.setButtonAccessibility();
    this.querySelector('.confirm-variant-button')?.addEventListener('click', this.confirmVariant.bind(this));
    this.updateSubmitButtonStatus();
    this.syncBundleInputs();

    // Cart attributes are only written when the bundle is actually added to
    // the cart, so merely viewing the page never stamps the customer's cart.
    this.bundleForm = document.getElementById('bundle-items-input')?.closest('form');
    this.handleBundleFormSubmit = this.handleBundleFormSubmit.bind(this);
    this.bundleForm?.addEventListener('submit', this.handleBundleFormSubmit);

    this.bundleProducts = JSON.parse(document.getElementById('product_bundle_sets').textContent);
    const JSONWithAsterisks = document.getElementById('bundle-delivery-date-json').textContent;
    const parsedJSON = JSON.parse(JSONWithAsterisks);

    this.stocks = {};
    this.inventories = {};

    // Convert all stock JSON strings and store by variant ID
    Object.keys(parsedJSON).forEach(variantId => {
      const stockJSON = parsedJSON[variantId].stock.replaceAll('*', '"');
      this.stocks[variantId] = JSON.parse(stockJSON);
      this.inventories[variantId] = parsedJSON[variantId].inventory;
    });

    // Close modal handlers
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#bundleModal-Overlay')?.addEventListener('click', this.close.bind(this));
    this.querySelector('.bundle-modal__close')?.addEventListener('click', this.close.bind(this));

    // Add click handler for modal content to close dropdown
    this.querySelector('.bundle-modal__content')?.addEventListener('click', this.handleModalClick.bind(this));
  }

  disconnectedCallback() {
    this.bundleForm?.removeEventListener('submit', this.handleBundleFormSubmit);
    this.removeEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#bundleModal-Overlay')?.removeEventListener('click', this.close.bind(this));
    this.querySelector('.bundle-modal__close')?.removeEventListener('click', this.close.bind(this));
    this.querySelector('.confirm-variant-button')?.removeEventListener('click', this.confirmVariant.bind(this));
    this.querySelector('.bundle-modal__content')?.removeEventListener('click', this.handleModalClick.bind(this));

    const colorSelector = this.querySelector('.color-selector');
    if (colorSelector && this.handleColorSelectorClick) {
      colorSelector.removeEventListener('click', this.handleColorSelectorClick);
    }
  }

  setButtonAccessibility() {
    const modalButtons = document.querySelectorAll('a[href="#bundle-item-variants-drawer"]');
    if (!modalButtons) return;

    modalButtons.forEach((modalButton) => {
      modalButton.addEventListener('click', async (event) => {
        event.preventDefault();

        const bundleIndex = parseInt(modalButton.dataset.bundleProductIndex) - 1;
        this.bundleToUpdateIndex = bundleIndex;

        // Initialize selectedVariant from previous selection or first variant
        this.selectedVariant = this.previouslySelectedVariant[bundleIndex] || null;

        const selectedBundleItem = modalButton.closest('.bundle-product-wrapper .bundle-item');
        const selectedBundleItemImage = selectedBundleItem.querySelector('.bundle-item-image');
        const selectedBundleItemTitle = selectedBundleItem.querySelector('.bundle-item-title');
        const selectedBundleItemSku = selectedBundleItem.dataset.bundleItemSku;

        this.bundleProductSet = this.bundleProducts[bundleIndex];

        // Use the first variant's image if no specific variant is selected
        const defaultVariant = this.bundleProductSet.product;
        const productData = {
          featured_image: selectedBundleItemImage.src || defaultVariant.featured_image || defaultVariant.image,
          title: selectedBundleItemTitle.textContent || defaultVariant.title,
          sku: selectedBundleItemTitle.textContent ? selectedBundleItemSku : defaultVariant.variants[0].sku
        };

        this.updateModalContent(productData);
        this.createVariantSelector();
        this.open(modalButton);

        // Update button state
        const isBundleItemChecked = selectedBundleItem.classList.contains('checked');
        const confirmVariantButton = this.querySelector('.confirm-variant-button');

        // Sets the button to disabled if no variant is selected yet
        // if (confirmVariantButton && !isBundleItemChecked) {
        //   confirmVariantButton.classList.add('disabled');
        // }
      });
    });
  }

  // Reads the current bundle selection from the DOM (confirmVariant keeps the
  // data attributes up to date, so the DOM is the single source of truth)
  collectBundleData() {
    const allBundleItems = Array.from(document.querySelectorAll('.bundle-product-wrapper .bundle-item'));

    // Initialize arrays with empty values for all positions
    const itemIds = new Array(allBundleItems.length).fill('');
    const itemHandles = new Array(allBundleItems.length).fill('');
    const itemPrices = new Array(allBundleItems.length).fill('');

    // Update only the checked positions
    allBundleItems.forEach((item, index) => {
      const isChecked = item.classList.contains('checked');
      const isFreeGift = item.dataset.bundleItemFreeGift === 'true';
      const price = item.dataset.bundleItemPrice;

      if (isChecked) {
        itemIds[index] = item.dataset.bundleItemId;
        itemHandles[index] = item.dataset.bundleItemHandle;
        itemPrices[index] = isFreeGift ? 0 : price;
      }
    });

    return { itemIds, itemHandles, itemPrices };
  }

  // Line item properties (hidden form inputs) — safe to update at any time,
  // they only reach the cart when the form is submitted
  syncBundleInputs() {
    const { itemIds, itemHandles, itemPrices } = this.collectBundleData();
    this.updateBundleItemsInput(itemIds, itemHandles, itemPrices);
  }

  // Private cart attributes (secure method) — written only at add-to-cart
  // time so viewing the page or picking variants never touches the cart
  handleBundleFormSubmit() {
    const { itemIds, itemHandles, itemPrices } = this.collectBundleData();
    this.updateCartAttributes(itemIds, itemHandles, itemPrices);
  }

  updateModalContent(productData) {
    // Update modal content with bundle product data
    const modalImage = this.querySelector('.product-image img');
    const modalTitle = this.querySelector('.product-info .product-title');
    const modalRatingSnippet = this.querySelector('.ruk_rating_snippet');

    if (modalImage && productData.featured_image) {
      modalImage.src = productData.featured_image;
      modalImage.alt = productData.title;
    }

    if (modalTitle) {
      modalTitle.textContent = productData.title;
    }

    if (modalRatingSnippet) {
      this.updateRatingSnippet(productData, modalRatingSnippet);
    }
  }

  createVariantSelector() {
    let variants = [this.bundleProductSet.product, ...this.bundleProductSet.variants];

    // Get the first variant as default if no variant is selected
    if (!this.selectedVariant) {
      this.selectedVariant = variants[0];
    }

    this.updateConfirmButtonStatus();

    // First, remove any existing event listeners
    const existingColorSelector = this.querySelector('.color-selector');
    if (existingColorSelector) {
      existingColorSelector.removeEventListener('click', this.handleColorSelectorClick);
    }

    let variantSelector = this.querySelector('.variant-selector__variants');
    if (!variantSelector) {
      variantSelector = document.createElement('div');
      variantSelector.classList.add('variant-selector__variants');
      this.querySelector('.variant-selector')?.appendChild(variantSelector);
    }

    // Clear existing variant options
    variantSelector.innerHTML = '';

    // Update the selected color display
    this.updateSelectedColorDisplay(this.selectedVariant);

    // Create variant options dynamically
    if (variants && variants.length > 0) {
      variants.forEach((variant) => {
        const variantOption = document.createElement('button');
        variantOption.classList.add('variant-option');
        variantOption.dataset.variantId = variant.id;
        variantOption.dataset.variantTitle = variant.title;

        const variantImage = variant.decor ? variant.decor.image : variant.images[0];
        const variantName = variant.decor ? variant.decor.name : variant.title;

        variantOption.innerHTML = `
          <img class="color-dot" src="${variantImage}" />
          <span class="variant-title ${variant.available ? '' : 'unavailable'}">${variantName}</span>
        `;

        variantOption.addEventListener('click', () => {
          // Store the temporary selection
          this.selectedVariant = variant;

          // Update modal content with the new variant
          const productData = {
            featured_image: variant.featured_image || variant.image,
            title: variant.title,
            sku: variant.variants ? variant.variants[0].sku : variant.sku
          };
          this.updateModalContent(productData);

          this.updateSelectedColorDisplay(variant);
          this.toggleVariantSelector();

          const productRatingSnippet = this.querySelector('.ruk_rating_snippet');
          this.updateRatingSnippet(variant, productRatingSnippet);

          this.updateConfirmButtonStatus();
        });

        variantSelector.appendChild(variantOption);
      });

      // Set up color selector with bound method
      const colorSelector = this.querySelector('.color-selector');
      if (colorSelector) {
        this.handleColorSelectorClick = (event) => {
          event.stopPropagation();
          this.toggleVariantSelector();
        };

        colorSelector.addEventListener('click', this.handleColorSelectorClick);
      }
    }
  }

  // New method to update the selected color display
  updateSelectedColorDisplay(variant) {
    const selectedColor = this.querySelector('.selected-color');
    if (selectedColor && variant) {
      const variantImage = variant.decor ? variant.decor.image : variant.images[0];
      const variantName = variant.decor ? variant.decor.name : variant.title;

      selectedColor.innerHTML = `
        <span class='color-name'>${variantName}</span>
        <img class="color-dot" src="${variantImage}" />
      `;
    }
  }

  /**
   * Returns the price for a variant in bundle context: uses bundle_price override (variant metafield) if set, else variant price.
   * @param {Object} productOrVariant - Product object with .variants[0] and optional .bundle_price (cents)
   * @returns {number} Price in cents
   */
  getVariantPrice(productOrVariant) {
    if (!productOrVariant?.variants?.[0]) return 0;
    const override = productOrVariant.bundle_price;
    if (override !== undefined && override !== null && override !== '') {
      return Number(override);
    }
    return productOrVariant.variants[0].price;
  }

  confirmVariant() {
    const selectedVariant = this.selectedVariant;
    this.previouslySelectedVariant[this.bundleToUpdateIndex] = this.selectedVariant;

    if (selectedVariant) {
      const bundleItemToUpdate = document.querySelectorAll('.bundle-product-wrapper .bundle-item')[this.bundleToUpdateIndex];

      bundleItemToUpdate.classList.add('checked');
      bundleItemToUpdate.dataset.bundleItemId = selectedVariant.variants[0].id;
      bundleItemToUpdate.dataset.bundleItemAvailable = selectedVariant.available;
      bundleItemToUpdate.dataset.bundleItemSku = selectedVariant.variants[0].sku;
      bundleItemToUpdate.dataset.bundleItemStock = JSON.stringify(this.stocks[selectedVariant.variants[0].id]);
      bundleItemToUpdate.dataset.bundleItemInventory = this.inventories[selectedVariant.variants[0].id];
      bundleItemToUpdate.dataset.bundleItemPrice = this.getVariantPrice(selectedVariant);
      bundleItemToUpdate.dataset.bundleItemHandle = selectedVariant.handle;

      // Preserve free gift status when changing variants
      const isFreeGift = this.bundleProductSet.is_free_gift || false;
      bundleItemToUpdate.dataset.bundleItemFreeGift = isFreeGift;

      const bundleItemButton = bundleItemToUpdate.querySelector('.bundle-item-variants-picker');
      bundleItemButton.classList.add('variant-selected');
      bundleItemButton.textContent = bundleItemButton.dataset.changeVariantText;

      const bundleItemImage = bundleItemToUpdate.querySelector('.bundle-item-image');
      bundleItemImage.src = selectedVariant.featured_image;
      bundleItemImage.alt = selectedVariant.title;

      const bundleItemTitle = bundleItemToUpdate.querySelector('.bundle-item-title');
      bundleItemTitle.textContent = selectedVariant.title;

      bundleItemButton.classList.remove('button', 'button--secondary');

      const bundleItemDecor = bundleItemToUpdate.querySelector('.bundle-item-variant-title');
      bundleItemDecor.textContent = selectedVariant.decor ? selectedVariant.decor.name : selectedVariant.title;

      const bundleItemLink = bundleItemToUpdate.querySelectorAll('.bundle-item-link');
      // Locale-aware product URL (routes.root_url carries the language prefix;
      // origin-based URLs dropped it and break on the subfolder-domain setup)
      const rootUrl = (window.routes?.root_url || '/').replace(/\/$/, '');
      bundleItemLink.forEach(link => {
        link.href = `${rootUrl}/products/${selectedVariant.handle}`;
      });

      const productRatingSnippet = bundleItemToUpdate.querySelector('.ruk_rating_snippet');
      this.updateRatingSnippet(selectedVariant, productRatingSnippet);

      this.updateBundlePrice();
      this.syncBundleInputs();
      this.updateSubmitButtonStatus();

      document.dispatchEvent(new CustomEvent('bundle-variant:update', { detail: { index: this.bundleToUpdateIndex } }));

      this.close();
    }
  }

  // Line item properties method (fallback compatibility)
  updateBundleItemsInput(itemIds, itemHandles, itemPrices) {
    const bundleItemsInput = document.getElementById('bundle-items-input');
    const bundleItemsHandlesInput = document.getElementById('bundle-items-handles-input');
    const bundleItemsPricesInput = document.getElementById('bundle-items-prices-input');

    // Update the form inputs with current bundle data
    if (bundleItemsInput) bundleItemsInput.value = itemIds.join(',');
    if (bundleItemsHandlesInput) bundleItemsHandlesInput.value = itemHandles.join(',');
    if (bundleItemsPricesInput) bundleItemsPricesInput.value = itemPrices.join(',');
  }

  // Update cart attributes with bundle data using private attributes for security
  async updateCartAttributes(itemIds, itemHandles, itemPrices) {
    try {
      const bundleDiscount = document.querySelector('.bundle-price')?.dataset?.bundleDiscount || '0';

      const cartAttributes = {
        attributes: {
          // Set private attributes (secure, hidden from customers)
          '__privateBundleItems': itemIds.join(','),
          '__privateBundleItemsHandles': itemHandles.join(','),
          '__privateBundleItemsPrices': itemPrices.join(','),
          '__privateBundleDiscount': bundleDiscount,
        }
      };

      const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(cartAttributes)
      });

      if (!response.ok) {
        console.error('Cart attributes update failed:', response.status);
      }

    } catch (error) {
      console.error('Error updating cart attributes:', error);
    }
  }



  updateBundlePrice() {
    const bundleItems = document.querySelectorAll('.bundle-product-wrapper .bundle-item');

    // Calculate total price INCLUDING free gifts for compare-at-price
    let bundleTotalPriceWithGifts = 0;
    // Calculate total price excluding free gifts for discounted price
    let bundleOriginalPrice = 0;

    Array.from(bundleItems).forEach(item => {
      const itemPrice = Number(item.dataset.bundleItemPrice);
      // Always include in total price with gifts
      bundleTotalPriceWithGifts += itemPrice;

      // Skip free gift items in discounted price calculation
      if (item.dataset.bundleItemFreeGift !== 'true') {
        bundleOriginalPrice += itemPrice;
      }
    });

    const bundleOriginalPriceElement = document.querySelector('.bundle-compare-at-price');

    // Use total price with gifts for compare-at-price
    const formattedPriceWithGifts = (bundleTotalPriceWithGifts / 100).toFixed(2).replace('.', ',');
    bundleOriginalPriceElement.textContent = this.currencyFormat == 'CHF' ? `CHF ${formattedPriceWithGifts}` : `${formattedPriceWithGifts}${this.currencyFormat}`;

    const bundleNewPriceElement = document.querySelector('.bundle-price');
    const bundleDiscount = Number(bundleNewPriceElement.dataset.bundleDiscount) / 100;
    // Use price excluding gifts for discounted price
    const formattedNewPrice = (bundleOriginalPrice * (1 - bundleDiscount) / 100).toFixed(2).replace('.', ',');

    bundleNewPriceElement.textContent = this.currencyFormat == 'CHF' ? `CHF ${formattedNewPrice}` : `${formattedNewPrice}${this.currencyFormat}`;

    const stickyATCPriceElement = document.querySelector('#price-sticky');
    const bundlePriceElement = stickyATCPriceElement.querySelector('.bundle-price');
    const bundleCompareAtElement = stickyATCPriceElement.querySelector('.bundle-compare-at-price');

    // Use total price with gifts for sticky compare-at-price
    bundleCompareAtElement.textContent = this.currencyFormat == 'CHF' ? `CHF ${formattedPriceWithGifts}` : `${formattedPriceWithGifts}${this.currencyFormat}`;
    // Use discounted price excluding gifts for sticky bundle price
    bundlePriceElement.textContent = this.currencyFormat == 'CHF' ? `CHF ${formattedNewPrice}` : `${formattedNewPrice}${this.currencyFormat}`;
  }

  updateConfirmButtonStatus() {
    const confirmVariantButton = this.querySelector('.confirm-variant-button');
    if (!confirmVariantButton) return;

    const isUnavailable = !this.selectedVariant?.available;
    confirmVariantButton.classList.toggle('disabled', isUnavailable);
    confirmVariantButton.disabled = isUnavailable;
  }

  updateSubmitButtonStatus() {
    const submitButton = document.querySelector('.product-form__submit.bundle-submit-button');
    const bundleItems = document.querySelectorAll('.bundle-product-wrapper .bundle-item');
    const areAllBundleItemsChecked = Array.from(bundleItems).every(item => item.classList.contains('checked'));
    const isAnyBundleItemUnavailable = Array.from(bundleItems).some(item => item.dataset.bundleItemAvailable === 'false');

    if (submitButton && areAllBundleItemsChecked && !isAnyBundleItemUnavailable) {
      submitButton.disabled = false;
    } else {
      submitButton.disabled = true;
    }

    if (isAnyBundleItemUnavailable) {
      const outOfStockText = submitButton.dataset.outOfStockText;
      submitButton.textContent = outOfStockText;
    } else {
      const addToCartText = submitButton.dataset.addToCartText;
      submitButton.textContent = addToCartText;
    }
  }

  toggleVariantSelector() {
    const variantSelector = this.querySelector('.variant-selector__variants');
    if (!variantSelector) {
      console.warn('Variant selector not found');
      return;
    }

    const isActive = variantSelector.classList.contains('active');
    if (isActive) {
      variantSelector.classList.remove('active');
    } else {
      variantSelector.classList.add('active');
    }
  }

  open() {
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });
  }

  close() {
    // Close the dropdown if it's open
    const variantSelector = this.querySelector('.variant-selector__variants');
    if (variantSelector?.classList.contains('active')) {
      variantSelector.classList.remove('active');
    }

    // Reset the temporary selection to the confirmed variant
    this.selectedVariant = this.previouslySelectedVariant[this.bundleToUpdateIndex];
    this.classList.remove('active');
  }

  // Add this new method to handle outside clicks
  handleOutsideClick(event) {
    const variantSelector = this.querySelector('.variant-selector__variants');
    const colorSelector = this.querySelector('.color-selector');

    // Check if click is outside both the variant selector and color selector
    if (variantSelector?.classList.contains('active') &&
      !variantSelector.contains(event.target) &&
      !colorSelector?.contains(event.target)) {
      variantSelector.classList.remove('active');
    }
  }

  // Update Reviews.io rating snippet
  async updateRatingSnippet(product, snippetToUpdate) {
    const productRating = await this.getProductRating(product);
    const avgRating = productRating[0] && productRating[0].average_rating || 0;
    const numRatings = productRating[0] && productRating[0].num_ratings || 0;

    // Calculate full stars and remaining percentage
    const fullStars = Math.floor(avgRating);
    const remainder = avgRating - fullStars;
    let partialStar = 0;

    // Convert remainder to 25/50/75/100
    if (remainder >= 0.87) partialStar = 100;
    else if (remainder >= 0.63) partialStar = 75;
    else if (remainder >= 0.38) partialStar = 50;
    else if (remainder >= 0.13) partialStar = 25;

    // Build stars HTML
    let starsHTML = '';

    // Add full stars
    for (let i = 0; i < fullStars; i++) {
      starsHTML += `<i aria-hidden="true" class="ruk-icon-percentage-star--100"></i>`;
    }

    // Add partial star if needed
    if (partialStar > 0 && fullStars < 5) {
      starsHTML += `<i aria-hidden="true" class="ruk-icon-percentage-star--${partialStar}"></i>`;
    }

    // Add empty stars
    const emptyStars = 5 - fullStars - (partialStar > 0 ? 1 : 0);
    for (let i = 0; i < emptyStars; i++) {
      starsHTML += `<i aria-hidden="true" class="ruk-icon-percentage-star--0"></i>`;
    }

    snippetToUpdate.innerHTML = starsHTML + `<span class="ruk-rating-snippet-count">(${numRatings})</span>`;
  }

  // Possible TODO: Performance optimization: Cache the product rating
  async getProductRating(product) {
    const productSku = product.sku || product.variants[0].sku;
    const options = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    };

    try {
      const response = await fetch(`https://api.reviews.io/product/rating-batch?store=reisenthel&sku=${productSku}`, options);
      const data = await response.json();
      return data;
    } catch (err) {
      console.error('Error fetching product rating:', err);
      return null;
    }
  }

  handleModalClick(event) {
    const variantSelector = this.querySelector('.variant-selector__variants');
    const colorSelector = this.querySelector('.color-selector');

    // If clicking outside the variant selector and color selector, close the dropdown
    if (variantSelector?.classList.contains('active') &&
      !variantSelector.contains(event.target) &&
      !colorSelector.contains(event.target)) {
      variantSelector.classList.remove('active');
    }
  }
}

customElements.define('bundle-product-modal', BundleProductModal);
